import { Drawer } from '@blueprintjs/core'
import { EventProperties } from '@segment/analytics-next'
import cx from 'classnames'
import dayjs from 'dayjs'
import { sortBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'

import {
  isWellImagingObservation,
  WellPage_PastObservation,
  WellPage_WellCulture,
  WellPageResponse,
} from '~/api/backend/pages/wellPageAPI'
import LinearScaleIcon from '~/components/icons/LinearScaleIcon'
import MontageHero from '~/components/MontageHero'
import PageNotFound from '~/components/PageNotFound'
import TinyMicroplate from '~/components/TinyMicroplate'
import {
  SUPPORTED_PLATE_DIMENSIONS,
  supportedPlateFormats,
} from '~/components/TinyMicroplate.interface'
import ZeroWidthSpaceToken from '~/components/ZeroWidthSpaceToken'
import { analytics } from '~/core/analytics'
import { useDemoQuery } from '~/demoControls/DemoContext'
import DemoDataRequiredFullPageError from '~/demoControls/DemoDataRequiredFullPageError'
import CultureLineage from '~/pages/Cle2025/CultureLineage/CultureLineage'
import { CultureStatusText } from '~/pages/Monitor/components/CultureStatusText'
import MontageImagesDialog from '~/pages/Monitor/pages/MontageImagesDialog/MontageImagesDialog'
import LateralNavCompass, {
  LateralNavAxisMode,
  LateralNavDirection,
} from '~/pages/plate/well/LateralNavCompass'
import WellConfluenceGraphSidebar from '~/pages/plate/well/WellConfluenceGraphSidebar'
import WellMetadataSidebar from '~/pages/plate/well/WellMetadataSidebar'
import { decodeURITimestamp, encodeURITimestamp } from '~/utils/date'
import {
  convertWellCoordsToWellName,
  convertWellNameToWellCoord,
} from '~/utils/microplate'

import BreadcrumbsCle2025 from './BreadcrumbsCle2025'
import cs from './well_page_cle_2025.scss'

export default function WellPageCle2025({
  plateID,
  pos,
  enableLineage = false,
  routePrefix = '',
}: {
  plateID: string
  pos: string
  enableLineage?: boolean
  routePrefix?: string
}) {
  const variables = { plateId: plateID, position: pos }
  const demoData = useDemoQuery(
    routePrefix ? 'cle' : 'monitor',
    'WellPage',
  )?.(variables)
  if (!demoData) {
    return <DemoDataRequiredFullPageError />
  }
  if (!demoData.culture) {
    return <PageNotFound />
  }
  return (
    <Content
      culture={demoData.culture}
      enableLineage={enableLineage}
      routePrefix={routePrefix}
      cultureData={demoData}
    />
  )
}

function Content({
  culture,
  enableLineage,
  routePrefix,
  cultureData,
}: {
  culture: NonNullable<WellPage_WellCulture>
  enableLineage: boolean
  routePrefix: string
  cultureData?: WellPageResponse
}) {
  const history = useHistory()

  // The index of the image the user clicked, or null if the image dialog is closed.
  // Note: This doesn't update when switching images within the dialog.
  const [openedImage, setOpenedImage] = useState<number | null>(null)

  const plateImageObservationHistory = culture.observationHistory.filter(
    isWellImagingObservation,
  )

  const sortedObservationHistory = useMemo(
    () => sortBy(plateImageObservationHistory, obs => obs.timestamp),
    [plateImageObservationHistory],
  )

  // Find the observation specified in the URL, defaulting to the latest one if
  // not specified.
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const decodedTime = decodeURITimestamp(params.get('t'))
  const decodedTimeISOString = decodedTime?.toISOString()
  let observationIndex = decodedTimeISOString
    ? sortedObservationHistory.findIndex(obs => obs.timestamp >= decodedTimeISOString)
    : -1
  if (observationIndex < 0) {
    observationIndex = sortedObservationHistory.length - 1
  }
  const observation = sortedObservationHistory[observationIndex]

  const [showLineage, setShowLineage] = useState(
    params.get('lineage') === 'show' && enableLineage,
  )

  useEffect(() => {
    analytics.page('Monitor', 'Well', {
      cultureID: culture.id,
      numCultureObservations: culture.observationHistory.length,
      observationTimestamp: observation?.timestamp,
    })
  }, [culture.id, observation?.timestamp])

  // Canonicalize the timestamp in the URL.
  useEffect(() => {
    const canonicalObservationTimestamp = observation
      ? encodeURITimestamp(dayjs(observation.timestamp))
      : ''
    if (params.get('t') !== canonicalObservationTimestamp) {
      const newParams = new URLSearchParams(params)
      if (decodedTime) {
        // URL has a valid timestamp, but it doesn't exactly match an
        // observation. Keep the param in the URL, but make it match the
        // observation that we landed on.
        newParams.set('t', canonicalObservationTimestamp)
      } else {
        // URL doesn't have a valid timestamp. Remove the param from the URL to
        // make it clear that we're not using it.
        newParams.delete('t')
      }
      history.replace({ search: newParams.toString() })
    }
  }, [observation?.timestamp])

  const plateDims = culture.culturePlate.plateDimensions
  const tinyMicroplateFormat = SUPPORTED_PLATE_DIMENSIONS.some(
    ({ rows, columns }) => rows === plateDims.rows && columns === plateDims.columns,
  )
    ? (`wells_${plateDims.rows * plateDims.columns}` as supportedPlateFormats)
    : null

  const parentCultures = useParentCultures(culture, routePrefix)

  const openCulture = useCallback(
    (plateId: string, well: string, timestamp: string) => {
      history.push(
        `/cle-2025/plate/${plateId}/well/${well}?t=${timestamp}&lineage=show`,
      )
    },
    [history],
  )

  return (
    <div>
      <BreadcrumbsCle2025
        plate={{ id: culture.culturePlate.id, data: culture.culturePlate }}
        wellPos={culture.well}
        routePrefix={routePrefix}
      />

      <div className={cs.main}>
        <div className={cs.titleArea}>
          <div>
            <div>
              <span className={cs.title}>Well {culture.well} </span>
              <ZeroWidthSpaceToken />
              <CultureStatusText status={culture.status} className={cs.status} />
            </div>
            {enableLineage && (
              <div>
                <a
                  className={cs.lineageButton}
                  onClick={() => {
                    const newParams = new URLSearchParams(params)
                    newParams.set('lineage', 'show')
                    history.replace({ search: newParams.toString() })
                    setShowLineage(true)
                  }}
                >
                  <LinearScaleIcon className={cs.lineageIcon} />
                  View Lineage
                </a>
              </div>
            )}
          </div>

          <LateralNavControls
            currentWell={culture.well}
            availableWells={culture.culturePlate.wellCultures.map(c => c.well)}
            tinyMicroplateFormat={tinyMicroplateFormat}
            onNavigate={toWell => {
              if (toWell !== culture.well) {
                history.replace(
                  `/cle-2025/plate/${culture.culturePlate.id}/well/${toWell}?${params.toString()}`,
                )
              }
            }}
          />
        </div>

        <div className={cs.heroToolbarArea}>
          <HeroToolbar
            observation={observation}
            page={{
              number: observationIndex + 1,
              total: sortedObservationHistory.length,
            }}
            onNavigate={dir => {
              let newIndex
              if (dir === LateralNavDirection.LEFT) {
                newIndex = Math.max(observationIndex - 1, 0)
              } else {
                newIndex = Math.min(
                  observationIndex + 1,
                  sortedObservationHistory.length - 1,
                )
              }
              const newTimestamp = sortedObservationHistory[newIndex].timestamp
              const newParams = new URLSearchParams(params)
              newParams.set('t', encodeURITimestamp(dayjs(newTimestamp)))
              history.replace({ search: newParams.toString() })
            }}
          />
        </div>

        <div className={cs.hero}>
          <MontageHero
            culture={culture}
            observationTimestamp={observation?.timestamp}
            onClickImage={
              observation?.montage
                ? idx => {
                    setOpenedImage(idx)
                  }
                : undefined
            }
          />
        </div>

        <div className={cs.sidebar}>
          <WellConfluenceGraphSidebar culture={culture} />
          <WellMetadataSidebar culture={culture} routePrefix={routePrefix} />
        </div>
      </div>

      {observation?.montage && openedImage != null ? (
        // Normally we'd keep this mounted and use the `isOpen` prop to control
        // its visibility. Here, as a hack, we unmount it when closed, so that
        // it always resets to the `initialImageIndex` when opened.
        <MontageImagesDialog
          montage={observation.montage}
          isOpen
          onClose={() => {
            setOpenedImage(null)
          }}
          initialImageIndex={openedImage ?? undefined}
          linkToCulture={false}
        />
      ) : null}

      {enableLineage ? (
        <Drawer
          isOpen={showLineage}
          onClose={() => {
            const newParams = new URLSearchParams(params)
            newParams.delete('lineage')
            history.replace({ search: newParams.toString() })
            setShowLineage(false)
          }}
          position='bottom'
          hasBackdrop={false}
          size='360px'
        >
          <CultureLineage
            isOpen={showLineage}
            onClose={() => {
              const newParams = new URLSearchParams(params)
              newParams.delete('lineage')
              history.replace({ search: newParams.toString() })
              setShowLineage(false)
            }}
            cultureData={cultureData}
            parentCultures={parentCultures}
            openCulture={openCulture}
            selectedTimestamp={observation?.timestamp}
          />
        </Drawer>
      ) : null}
    </div>
  )
}

function LateralNavControls({
  currentWell,
  availableWells,
  tinyMicroplateFormat,
  onNavigate,
}: {
  currentWell: string
  availableWells: string[]
  tinyMicroplateFormat: supportedPlateFormats | null
  onNavigate: (toWell: string) => void
}) {
  const availableWellsSet = useMemo(() => new Set(availableWells), [availableWells])
  const nextWellFor = useWellNavOrdering(currentWell, availableWells)
  const [hoveredWell, setHoveredWell] = useState<string | null>(null)

  const trackNavigatedAnalyticsEvent = (using: string, extra: EventProperties) => {
    analytics.track('Navigated from well to well', {
      using,
      plateFormat: tinyMicroplateFormat,
      ...extra,
    })
  }

  return (
    <div className={cs.lateralNavControls}>
      {
        // For visual stability, we keep showing the label regardless if the
        // user is hovering the minimap or the compass. But, to make it clear
        // that unavailable wells cannot be navigated to, we temporarily hide
        // the label if hovering over an unavailable well.
        !hoveredWell || availableWellsSet.has(hoveredWell) ? (
          <div
            className={cx(cs.label, {
              [cs.active]: !hoveredWell || hoveredWell === currentWell,
            })}
          >
            {hoveredWell ?? currentWell}
          </div>
        ) : null
      }

      {tinyMicroplateFormat ? (
        <TinyMicroplate
          className={cs.tinyMicroplate}
          plateFormat={tinyMicroplateFormat}
          highlights={[
            {
              colorRgbFn: (row: number, col: number) => {
                const well = convertWellCoordsToWellName(row, col)
                if (well === currentWell) {
                  return '#2cb1bc' // Accent
                }
                if (availableWellsSet.has(well)) {
                  return '#ccc' // Grey Light
                }
                return '#eaeaea' // Grey Lightest
              },
            },
          ]}
          onClickCell={(rowIndex, colIndex) => {
            const well = convertWellCoordsToWellName(rowIndex, colIndex)
            if (availableWellsSet.has(well)) {
              onNavigate(well)
              trackNavigatedAnalyticsEvent('minimap', { toWell: well })
            }
          }}
          onMouseOverCell={(rowIndex, colIndex) => {
            setHoveredWell(convertWellCoordsToWellName(rowIndex, colIndex))
          }}
          onMouseLeave={() => {
            setHoveredWell(null)
          }}
        />
      ) : null}

      <div className={cs.compassContainer}>
        <LateralNavCompass
          onNavigate={direction => {
            onNavigate(nextWellFor(direction))
            trackNavigatedAnalyticsEvent('direction_buttons', { direction })
          }}
        />
      </div>
    </div>
  )
}

/**
 * Return a function that returns the next well in a particular 2D direction.
 *
 * If there are no available wells, the returned function always returns the
 * current well.
 */
function useWellNavOrdering(
  currentWell: string,
  availableWells: string[],
): (dir: LateralNavDirection) => string {
  const orderings = useMemo(() => {
    const availableCoords = availableWells.map(well => convertWellNameToWellCoord(well))

    // Horizontal navigation uses row-major order: [A1, A2, A3, B1, B2, B3]
    const horizontal = availableCoords
      .toSorted((a, b) => a.colIndex - b.colIndex)
      .toSorted((a, b) => a.rowIndex - b.rowIndex)
      .map(({ rowIndex, colIndex }) => convertWellCoordsToWellName(rowIndex, colIndex))

    // Vertical navigation uses column-major order: [A1, B1, A2, B2, A3, B3]
    const vertical = availableCoords
      .toSorted((a, b) => a.rowIndex - b.rowIndex)
      .toSorted((a, b) => a.colIndex - b.colIndex)
      .map(({ rowIndex, colIndex }) => convertWellCoordsToWellName(rowIndex, colIndex))

    return { horizontal, vertical }
  }, [availableWells])

  const movement: { [key in LateralNavDirection]: [string[], number] } = {
    [LateralNavDirection.UP]: [orderings.vertical, -1],
    [LateralNavDirection.DOWN]: [orderings.vertical, +1],
    [LateralNavDirection.LEFT]: [orderings.horizontal, -1],
    [LateralNavDirection.RIGHT]: [orderings.horizontal, +1],
  }

  return (direction: LateralNavDirection) => {
    const [order, step] = movement[direction]
    if (order.length === 0) {
      return currentWell
    }

    const currentIndex = order.indexOf(currentWell)
    let newIndex = (currentIndex + step) % order.length
    if (newIndex === -1) {
      newIndex = order.length - 1
    }
    return order[newIndex]
  }
}

function HeroToolbar({
  observation,
  page,
  loading,
  onNavigate,
}: {
  observation?: WellPage_PastObservation
  page?: { number: number; total: number }
  loading?: boolean
  onNavigate?: (dir: LateralNavDirection.LEFT | LateralNavDirection.RIGHT) => void
}) {
  const time = observation ? dayjs(observation.timestamp) : null
  const isThisYear = time?.year() === dayjs().year()

  const enabledDirections = [
    page != null && page.number > 1 ? LateralNavDirection.LEFT : null,
    page != null && page.number < page.total ? LateralNavDirection.RIGHT : null,
  ].filter(d => d != null)

  return (
    <div className={cs.heroToolbarContent}>
      <div>
        {time ? (
          <>
            {time.format(isThisYear ? 'MMM D' : 'MMM D, YYYY')}&ensp;
            {time.format('h:mm A')} ({time.fromNow()})
          </>
        ) : loading ? (
          <>&nbsp;</>
        ) : (
          <>No images yet.</>
        )}
      </div>

      <div className={cs.pager}>
        {page?.total ? (
          <span>
            Observation {page.number} of {page.total}
          </span>
        ) : page ? (
          <span>No observations</span>
        ) : null}

        <LateralNavCompass
          axes={LateralNavAxisMode.HORIZONTAL}
          onNavigate={d => {
            onNavigate?.(d as LateralNavDirection.LEFT | LateralNavDirection.RIGHT)
          }}
          enable={enabledDirections}
        />
      </div>
    </div>
  )
}

const useParentCultures = (
  initialCulture: WellPage_WellCulture,
  routePrefix: string,
) => {
  const [parentCultures, setParentCultures] = useState<WellPage_WellCulture[]>([])
  const demoQuery = useDemoQuery(routePrefix ? 'cle' : 'monitor', 'WellPage')

  useEffect(() => {
    const loadParentCultures = async (
      culture: WellPage_WellCulture,
      accumulated: WellPage_WellCulture[] = [],
    ) => {
      if (!culture?.parentCulture?.id) {
        setParentCultures(accumulated)
        return
      }

      // Extract plate ID and well position from parent culture
      const parentPlateId = culture.parentCulture.culturePlate?.id
      const parentWell = culture.parentCulture.well

      if (!parentPlateId || !parentWell) {
        setParentCultures(accumulated)
        return
      }

      // Use the demo query to get the parent culture data
      const parentData = demoQuery?.({
        plateId: parentPlateId,
        position: parentWell,
      })

      if (parentData?.culture) {
        const newAccumulated = [...accumulated, parentData.culture]
        await loadParentCultures(parentData.culture, newAccumulated)
      } else {
        setParentCultures(accumulated)
      }
    }

    loadParentCultures(initialCulture, [initialCulture])
  }, [initialCulture, demoQuery])

  return parentCultures
}
